<script setup>
import { computed } from "vue";
import SettingsField from "./SettingsField.vue";
import { billNumberPreview } from "./companySettingsRules";
import { useCompanySettings } from "./useCompanySettings";

const { form } = useCompanySettings();

const salesPreview = computed(() => billNumberPreview(form.InvoicePrefix));
const purchasePreview = computed(() => billNumberPreview(form.PurchasePrefix));
const addressLine = computed(() =>
  [form.AddressLine1, form.AddressLine2, form.City, form.State && `${form.State} ${form.PinCode || ""}`.trim()].filter(Boolean).join(", ")
);
</script>

<template>
  <div class="cs-panel">
    <section class="cs-group">
      <header class="cs-group-head">
        <h5>Numbering</h5>
        <p>The prefix starts every bill number. Numbers restart each month within the financial year.</p>
      </header>
      <div class="row g-3">
        <div class="col-md-6">
          <SettingsField field="InvoicePrefix" placeholder="INV" maxlength="10" uppercase>
            <template #after>
              <div class="cs-number-preview">Next sales bill looks like <code>{{ salesPreview }}</code></div>
            </template>
          </SettingsField>
        </div>
        <div class="col-md-6">
          <SettingsField field="PurchasePrefix" placeholder="PUR" maxlength="10" uppercase>
            <template #after>
              <div class="cs-number-preview">Next purchase looks like <code>{{ purchasePreview }}</code></div>
            </template>
          </SettingsField>
        </div>
      </div>
      <p class="cs-note">
        <vue-feather type="info" size="14"></vue-feather>
        Changing a prefix only affects new bills. Existing bill numbers stay as they are.
      </p>
    </section>

    <section class="cs-group">
      <header class="cs-group-head">
        <h5>Printed text</h5>
        <p>Shown at the bottom of every invoice.</p>
      </header>
      <div class="row g-3">
        <div class="col-md-6">
          <SettingsField field="InvoiceFooter" type="textarea" placeholder="e.g. Thank you for shopping with us!" maxlength="500" />
        </div>
        <div class="col-md-6">
          <SettingsField
            field="InvoiceTerms"
            type="textarea"
            placeholder="e.g. Goods once sold will not be taken back. Subject to Kochi jurisdiction."
            maxlength="500"
          />
        </div>
      </div>
    </section>

    <div class="cs-invoice-preview" aria-label="Invoice preview">
      <div class="cs-preview-title">Invoice preview</div>
      <div class="cs-invoice-head">
        <img v-if="form.LogoUrl" :src="form.LogoUrl" alt="" />
        <div>
          <strong>{{ form.CompanyName || "Your company name" }}</strong>
          <div>{{ addressLine || "Your address" }}</div>
          <div>
            <span v-if="form.Phone1">Ph: {{ form.Phone1 }}</span>
            <span v-if="form.Email"> · {{ form.Email }}</span>
          </div>
          <div v-if="form.GSTIN">GSTIN: {{ form.GSTIN }}</div>
        </div>
        <div class="cs-invoice-no">
          <small>Invoice no.</small>
          <div>{{ salesPreview }}</div>
        </div>
      </div>
      <div class="cs-invoice-body">Items, tax and totals…</div>
      <div class="cs-invoice-foot">
        <div v-if="form.InvoiceFooter">{{ form.InvoiceFooter }}</div>
        <div v-if="form.InvoiceTerms" class="text-muted">{{ form.InvoiceTerms }}</div>
        <div v-if="!form.InvoiceFooter && !form.InvoiceTerms" class="text-muted">Footer and terms appear here.</div>
      </div>
    </div>
  </div>
</template>
